import { useCallback, useEffect, useRef, useState } from 'react';

import { reportMessage } from '@/integrations/sentry/errorReporting';

import type { TimeWindowMicros } from '../../api/time-window/timeWindow';
import { SLOWNESS_REPORTING_ENABLED, SLOWNESS_SAMPLE_RATE } from '../config/slownessConfig';
import type { DiagramSlowness, DiagramSlownessKind } from '../types/DiagramSlowness.types';
import { isSlownessReportDue } from '../utils/diagramSlowness';
import { isSessionSampled } from '../utils/slownessSampling';
import { useSlowGestureDetection } from './useSlowGestureDetection';
import { useSlowRenderDetection } from './useSlowRenderDetection';
import { useSlowSelectionDetection } from './useSlowSelectionDetection';

type UseDiagramSlownessReportParams = {
  targetId: number | undefined;
  timeWindow: TimeWindowMicros | undefined;
  requestedSeriesIds: string[];
  chartElement: HTMLElement | null;
  selectorElement: HTMLElement | null;
};

type ReportContext = {
  selectedSeriesCount: number;
  timeWindowDays: number | undefined;
};

const MICROS_PER_DAY = 86_400_000_000;

/** Per page load, so closing and reopening the diagram does not start the count again. */
const occurrenceCounts = new Map<DiagramSlownessKind, number>();

function getTimeWindowDays(timeWindow: TimeWindowMicros | undefined): number | undefined {
  return timeWindow && Math.round((timeWindow.toTimeMicros - timeWindow.fromTimeMicros) / MICROS_PER_DAY);
}

/** Whether a weak machine could be the reason; coarse and not identifying. */
function getDeviceContext() {
  const { hardwareConcurrency, deviceMemory } = navigator as Navigator & { deviceMemory?: number };

  return { cpuThreads: hardwareConcurrency, deviceMemoryGb: deviceMemory };
}

function roundMeasurements({ kind, ...measurements }: DiagramSlowness) {
  return {
    kind,
    ...Object.fromEntries(Object.entries(measurements).map(([name, value]) => [name, Math.round(value)])),
  };
}

/**
 * Tells Sentry when the diagram was unusably slow: rendering, zooming, panning or ticking a series.
 * Each kind is sent at most once per page load, and only after it kept happening (see
 * SLOWNESS_OCCURRENCES_TO_REPORT), so normal use sends nothing. Only a sample of sessions is measured
 * at all (SLOWNESS_SAMPLE_RATE), and SLOWNESS_REPORTING_ENABLED switches the whole thing off.
 */
export function useDiagramSlownessReport({
  targetId,
  timeWindow,
  requestedSeriesIds,
  chartElement,
  selectorElement,
}: UseDiagramSlownessReportParams) {
  const [isMeasured] = useState(() => SLOWNESS_REPORTING_ENABLED && isSessionSampled(SLOWNESS_SAMPLE_RATE));
  const contextRef = useRef<ReportContext>({ selectedSeriesCount: 0, timeWindowDays: undefined });

  useEffect(() => {
    contextRef.current = {
      selectedSeriesCount: requestedSeriesIds.length,
      timeWindowDays: getTimeWindowDays(timeWindow),
    };
  }, [requestedSeriesIds.length, timeWindow]);

  // Stable, so the detectors keep watching instead of restarting whenever the selection changes.
  const reportSlowness = useCallback((slowness: DiagramSlowness) => {
    const occurrenceCount = (occurrenceCounts.get(slowness.kind) ?? 0) + 1;

    occurrenceCounts.set(slowness.kind, occurrenceCount);

    if (isSlownessReportDue(slowness.kind, occurrenceCount)) {
      reportMessage(`Diagram unusably slow: ${slowness.kind}`, 'warning', {
        ...roundMeasurements(slowness),
        occurrenceCount,
        ...contextRef.current,
        sampleRate: SLOWNESS_SAMPLE_RATE,
        ...getDeviceContext(),
      });
    }
  }, []);

  useSlowRenderDetection({
    targetId,
    timeWindow,
    requestedSeriesIds,
    enabled: isMeasured,
    onSlowRender: reportSlowness,
  });
  useSlowGestureDetection({ chartElement: isMeasured ? chartElement : null, onSlowGesture: reportSlowness });
  useSlowSelectionDetection({ selectorElement: isMeasured ? selectorElement : null, onSlowSelection: reportSlowness });
}
